import React, { useEffect, useState, useMemo } from 'react';
import { Accordion } from '@ds';
import { IOption } from '@design-system/elements/Select/Select.types';
import { useGetDropdownItems } from 'src/shared/queries/useGetDropdownItems';
import { useUserProfileStore } from 'src/stores/UserProfile';
import PhoneInput, { ContactPhone } from 'src/components/PhoneInput/PhoneInput';
import { processUpdatedDetails } from 'src/pages/UserProfile/UserProfile.service';

const INITIAL_CONTACT_PHONE: ContactPhone = {
  isPrimary: false,
};

const PhoneContactFields = ['phoneType', 'countryCode', 'phoneNumber', 'extension'];

interface IPhoneContacts {
  contactRefId: number;
  phones: ContactPhone[];
}

const PhoneContacts: React.FC<IPhoneContacts> = (props) => {
  const { phones, contactRefId } = props;
  const { editMode, updatedFields, setUpdatedFields } = useUserProfileStore();
  const [contactPhones, setContactPhones] = useState<ContactPhone[]>([{ ...INITIAL_CONTACT_PHONE }]);
  const [activeIndexes, setActiveIndexes] = useState<number[]>([0]);
  const [contactPhonesTabs, setContactPhonesTabs] = useState<any[]>([]);
  const { data: phoneTypes } = useGetDropdownItems('PHONE_TYPE');

  const phoneTypeOptions = useMemo(
    () => phoneTypes?.map(({ type }) => ({ value: type, label: type })) || [],
    [phoneTypes]
  );

  useEffect(() => {
    let list = [INITIAL_CONTACT_PHONE];
    if (phones?.length > 0) {
      list = JSON.parse(JSON.stringify(phones));
    }
    setContactPhones(list);
  }, [phones]);

  useEffect(() => {
    if (editMode) {
      const updatedPhones = processUpdatedDetails(
        contactPhones,
        phones.map((phone) => ({ ...phone })),
        { contactRefId },
        'phoneId',
        PhoneContactFields
      );
      const newFields = { ...updatedFields };
      const hasUpdatedPhones = Object.values(updatedPhones).some((list) => list?.length > 0);
      if (hasUpdatedPhones) {
        newFields.contactPhones = updatedPhones;
      } else {
        delete newFields.contactPhones;
      }
      setUpdatedFields(Object.keys(newFields)?.length > 0 ? newFields : null);
    }
  }, [editMode, contactRefId, phones, contactPhones]);

  const handlePhoneContactFieldChange = (index: number, fieldName: string, value: string) => {
    const updatedContactPhones = [...contactPhones];
    updatedContactPhones[index] = {
      ...updatedContactPhones[index],
      [fieldName]: value,
    };
    setContactPhones(updatedContactPhones);
  };

  const handlePhoneTypeChange = (index: number, selectedOption: IOption) => {
    const updatedContactPhones = [...contactPhones];
    updatedContactPhones[index] = {
      ...updatedContactPhones[index],
      phoneType: (selectedOption?.value as string) || '',
    };
    setContactPhones(updatedContactPhones);
  };

  const handleIsPrimaryChange = (index: number) => {
    const updatedContactPhones = contactPhones.map((phone, phoneIndex) => ({
      ...phone,
      isPrimary: phoneIndex === index,
    }));
    setContactPhones(updatedContactPhones);
  };

  const handleAddAnotherContactPhone = () => {
    setActiveIndexes([contactPhones.length]);
    setContactPhones((prevState) => [...prevState, { ...INITIAL_CONTACT_PHONE }]);
  };

  const handleDeleteContactPhone = async (index: number) => {
    const contact = contactPhones[index];
    const updatedContactPhones = [...contactPhones];
    updatedContactPhones.splice(index, 1);
    const isPrimaryContact = contact.isPrimary && updatedContactPhones.length > 0;
    if (isPrimaryContact) {
      updatedContactPhones[0].isPrimary = true;
    }
    setContactPhones(updatedContactPhones);
    if (updatedContactPhones.length === 1) {
      setActiveIndexes([0]);
    }
  };

  useEffect(() => {
    if (!contactPhones || !phoneTypeOptions) return;

    const tabs = contactPhones.map(({ phoneId, isPrimary, phoneNumber, countryCode, phoneType, extension }, index) => {
      const refPhone = phones?.find((phone) => phone.phoneId === phoneId);
      return {
        body: (
          <PhoneInput
            isPrimary={isPrimary}
            phoneNumber={phoneNumber}
            countryCode={countryCode}
            phoneType={phoneType}
            extension={extension}
            phoneTypeOptions={phoneTypeOptions}
            id={index}
            refPhone={refPhone}
            canEdit={editMode}
            titles={{ phoneType: 'Type' }}
            onChangeField={handlePhoneContactFieldChange}
            onChangeSelect={handlePhoneTypeChange}
            onPrimaryChange={handleIsPrimaryChange}
          />
        ),
      };
    });
    setContactPhonesTabs(tabs);
  }, [contactPhones, phoneTypeOptions, phones, editMode]);

  return (
    <Accordion
      multiple
      allowToDelete={true}
      isDeleteDisabled={!editMode}
      shouldPreserveFirstTab
      tabs={contactPhonesTabs}
      activeIndex={activeIndexes}
      onTabChange={({ index }) => setActiveIndexes(index as number[])}
      onDeleteTab={handleDeleteContactPhone}
      tabHeaderPrefix="PHONE NUMBERS"
      additionalTabConfig={{
        buttonLabel: 'PHONE NUMBER',
        onAdd: handleAddAnotherContactPhone,
        disableAdd: !editMode,
      }}
    />
  );
};

export default PhoneContacts;
